import React, { useState } from 'react';
import { Mail, Phone, MapPin, ArrowRight, Heart, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const FinalCTA = () => {
  const [email, setEmail] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    toast({
      title: 'Welcome to the movement!',
      description: 'You\u2019ll receive stories and updates from ACCI soon. (demo)',
    });
    setEmail('');
  };

  return (
    <section id="contact" className="py-24 lg:py-28 bg-[#6e12ed] relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#79c4c7]/30 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
        <h2 className="font-serif text-white text-4xl lg:text-6xl font-bold leading-[1.02]">
          Ready to <span className="hl-box hl-cyan">stand</span><br />
          with us?
        </h2>
        <p className="mt-6 text-white/90 text-lg lg:text-xl max-w-2xl mx-auto">
          Join the conversation. Get campaign updates, member stories and invitations to
          community events — straight to your inbox.
        </p>

        <form onSubmit={submit} className="mt-10 max-w-xl mx-auto flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 h-14 px-6 rounded-md bg-white text-[#0E1112] placeholder:text-neutral-400 focus:outline-none focus:ring-4 focus:ring-white/40"
          />
          <button
            type="submit"
            className="h-14 px-7 bg-[#0E1112] hover:bg-black text-white font-bold rounded-md group inline-flex items-center justify-center gap-2"
          >
            Join the Movement
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-white/90 text-sm">
          <a href="tel:01902571230" className="inline-flex items-center gap-2 hover:text-white link-underline">
            <Phone className="w-4 h-4" /> 01902 571230
          </a>
          <a href="mailto:support@acci.org.uk" className="inline-flex items-center gap-2 hover:text-white link-underline">
            <Mail className="w-4 h-4" /> support@acci.org.uk
          </a>
          <span className="inline-flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Wolverhampton, UK
          </span>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#0E1112] text-white/80 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img
              src="/assets/acci-logo.png"
              alt="ACCI \u2014 African Caribbean Community Initiative"
              className="h-16 w-auto bg-white rounded-lg p-2"
            />
            <p className="mt-5 text-sm leading-relaxed max-w-md text-white/70">
              Since 1987, ACCI has been a globally recognised leader in culturally responsive,
              holistic mental health care — built for and with the Black Country community.
            </p>
            <div className="mt-6 flex gap-3">
              {[Facebook, Instagram, Twitter, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#6e12ed] flex items-center justify-center transition-colors"
                  aria-label="Social link"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="font-serif text-white font-bold mb-4">Explore</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#about" className="hover:text-[#79c4c7]">About Us</a></li>
              <li><a href="#take-part" className="hover:text-[#79c4c7]">Take Part</a></li>
              <li><a href="#impact" className="hover:text-[#79c4c7]">Our Impact</a></li>
              <li><a href="#stories" className="hover:text-[#79c4c7]">Stories</a></li>
              <li><a href="#donate" className="hover:text-[#79c4c7]">Donate</a></li>
            </ul>
          </div>

          <div>
            <div className="font-serif text-white font-bold mb-4">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#79c4c7]" /> 01902 571230</li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#79c4c7]" /> support@acci.org.uk</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#79c4c7]" /> Wolverhampton, Black Country, UK</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} African Caribbean Community Initiative. Registered Charity.</div>
          <div className="flex items-center gap-1">Made with <Heart className="w-3 h-3 text-[#E63946] fill-[#E63946]" /> for the community.</div>
        </div>
      </div>
    </footer>
  );
};

export { FinalCTA, Footer };
